import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { FaFilter, FaFileCsv } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { ButtonCmp } from '../../components/ButtonCmp'
import { appApi } from '../../services/appApi'
import { useAuth } from '../../contexts/AuthContext'
import { getToken } from '../../services/token'
import { Body } from '../Layouts/Body'
import { DrawerFiltro, iFiltroLogAuditoria } from './components/DrawerFiltro'
import { ModalPayload } from './components/ModalPayload'
import { usePermissaoLog } from './permissao'

interface iLogAuditoria {
  usuario: string
  usuarioNome: string
  timestamp: string
  metodo: string
  rota: string
  statusCode: number
  rowKey: string
  partitionKey: string
  temPayload: boolean
}

export const LogAuditoria: React.FC = () => {
  const navigate = useNavigate()
  const { ListaEmpresas } = useAuth()
  const { carregando, permitido, todasEmpresas } = usePermissaoLog()
  const { isOpen: isFiltroOpen, onOpen: onFiltroOpen, onClose: onFiltroClose } = useDisclosure()
  const { isOpen: isPayloadOpen, onOpen: onPayloadOpen, onClose: onPayloadClose } = useDisclosure()

  const [Logs, setLogs] = useState<iLogAuditoria[]>([])
  const [Loading, setLoading] = useState(true)
  const [Erro, setErro] = useState(false)
  const [Filtro, setFiltro] = useState<iFiltroLogAuditoria>({ dataInicio: '', dataFim: '', usuario: '', empresaId: '' })
  const [Payload, setPayload] = useState('')
  const [PayloadLoading, setPayloadLoading] = useState(false)

  useEffect(() => {
    if (carregando) return
    if (!permitido) {
      navigate('/')
      return
    }
    carregarLogs(Filtro)
  }, [carregando, permitido])

  function carregarLogs(filtro: iFiltroLogAuditoria): void {
    setLoading(true)
    setErro(false)
    const params: Record<string, string> = {}
    if (filtro.dataInicio) params.dataInicio = filtro.dataInicio
    if (filtro.dataFim) params.dataFim = filtro.dataFim
    if (filtro.usuario) params.usuario = filtro.usuario
    if (filtro.empresaId) params.empresaId = filtro.empresaId

    appApi.get('/Auditoria/Log', { params })
      .then(res => {
        setLogs(res.data)
        setLoading(false)
      })
      .catch(() => {
        setErro(true)
        setLoading(false)
      })
  }

  function abrirPayload(log: iLogAuditoria): void {
    if (!log.temPayload) return
    onPayloadOpen()
    setPayloadLoading(true)
    setPayload('')
    appApi.get(`/Auditoria/Log/${log.rowKey}/Payload`, { params: { data: log.partitionKey } })
      .then(res => {
        try {
          const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data
          setPayload(JSON.stringify(parsed, null, 2))
        } catch {
          setPayload(String(res.data))
        }
        setPayloadLoading(false)
      })
      .catch(() => {
        setPayload('Não foi possível carregar o payload')
        setPayloadLoading(false)
      })
  }

  function exportarCsv(): void {
    const params = new URLSearchParams()
    params.append('token', getToken())
    if (Filtro.dataInicio) params.append('dataInicio', Filtro.dataInicio)
    if (Filtro.dataFim) params.append('dataFim', Filtro.dataFim)
    if (Filtro.usuario) params.append('usuario', Filtro.usuario)
    if (Filtro.empresaId) params.append('empresaId', Filtro.empresaId)
    window.location.href = `${process.env.REACT_APP_URL_API}/Auditoria/Log/Csv?${params.toString()}`
  }

  function tratarData(data: string): string {
    if (!data) return ''
    const d = new Date(data)
    if (isNaN(d.getTime())) return data
    return d.toLocaleString('pt-BR')
  }

  if (carregando || !permitido) {
    return null
  }

  return (
    <Body isLoading={Loading}>
      <DrawerFiltro
        isOpen={isFiltroOpen}
        onClose={onFiltroClose}
        startValue={Filtro}
        empresas={todasEmpresas ? ListaEmpresas : undefined}
        onRedefinir={() => {
          const filtroVazio = { dataInicio: '', dataFim: '', usuario: '', empresaId: '' }
          setFiltro(filtroVazio)
          carregarLogs(filtroVazio)
          onFiltroClose()
        }}
        onFilter={(filtro) => {
          setFiltro(filtro)
          carregarLogs(filtro)
          onFiltroClose()
        }}
      />

      <ModalPayload
        isOpen={isPayloadOpen}
        onClose={onPayloadClose}
        isLoading={PayloadLoading}
        payload={Payload}
      />

      <Flex direction={'column'} padding={'1rem'} borderRadius={'2xl'} boxShadow={'base'} backgroundColor={'white'}>
        <Flex justifyContent={'space-between'} alignItems={'center'} mb={'1rem'}>
          <Text fontSize={'lg'} fontWeight={'bold'}>Log de auditoria</Text>
          <Flex gap={'.5rem'}>
            <ButtonCmp VarColor='c6' leftIcon={<FaFileCsv />} onClick={exportarCsv}>Exportar CSV</ButtonCmp>
            <ButtonCmp VarColor='Rosa' leftIcon={<FaFilter />} onClick={onFiltroOpen}>Filtros</ButtonCmp>
          </Flex>
        </Flex>

        {Erro && <Text fontSize={'sm'} color={'red.500'}>Não foi possível carregar os logs</Text>}

        {!Erro && (
          <Box overflowX={'auto'}>
            <Table size={'sm'}>
              <Thead>
                <Tr>
                  <Th fontSize={'.7rem'}>Usuário</Th>
                  <Th fontSize={'.7rem'}>Data</Th>
                  <Th fontSize={'.7rem'}>Método</Th>
                  <Th fontSize={'.7rem'}>Rota</Th>
                  <Th fontSize={'.7rem'}>Status Code</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Logs.map((log, i) => (
                  <Tr
                    key={i}
                    cursor={log.temPayload ? 'pointer' : 'default'}
                    opacity={log.temPayload ? 1 : 0.5}
                    _hover={log.temPayload ? { backgroundColor: 'var(--c2)' } : undefined}
                    onClick={() => abrirPayload(log)}
                    title={log.temPayload ? '' : 'Este registro não tem payload salvo'}
                  >
                    <Td fontSize={'.75rem'}>{log.usuarioNome || '-'} ({log.usuario})</Td>
                    <Td fontSize={'.75rem'}>{tratarData(log.timestamp)}</Td>
                    <Td fontSize={'.75rem'}>{log.metodo}</Td>
                    <Td fontSize={'.75rem'}>{log.rota}</Td>
                    <Td fontSize={'.75rem'}>{log.statusCode}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {Logs.length === 0 && !Loading && <Text fontSize={'.75rem'} textAlign={'center'} mt={'1rem'}>Nenhum registro encontrado</Text>}
          </Box>
        )}
      </Flex>
    </Body>
  )
}
